/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import {
  EditingState,
  SortingState,
  IntegratedSorting,
  DataTypeProvider,
  SearchState,
  IntegratedFiltering,
  PagingState,
  IntegratedPaging,
} from '@devexpress/dx-react-grid';
import {
  Grid,
  TableHeaderRow,
  TableEditColumn,
  SearchPanel,
  Toolbar,
  DragDropProvider,
  Table,
  TableColumnReordering,
  TableColumnResizing,
  TableColumnVisibility,
  ColumnChooser,
  PagingPanel,
} from '@devexpress/dx-react-grid-material-ui';
import { Command, PopupEditing } from '../../Shared';
import { getRowId, updateDocNumbers, userroles } from '../../common';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  createGeneralDoc,
  deleteGeneralDoc,
  getGeneralDocs,
  updateGeneralDoc,
} from '../../graphql';
import {
  attachmentsFormatter,
  currencyFormatter,
  opTypeFormatter,
  samllFormatter,
  timeFormatter,
} from '../../Shared/colorFormat';
import { SearchTable } from '../../components';
import { Typography } from '@material-ui/core';
import { TableComponent } from '../../Shared/TableComponent';
import { useTemplate } from '../../hooks';
import PopupDocKaid from '../../pubups/PopupDocKaid';

const DocKaid = forwardRef((props: any, ref: any) => {
  const { isRTL, words, start, end, company, theme } = props;

  const [rows, setRows] = useState([]);
  const [columns] = useState([
    { name: 'time', title: words?.time },
    { name: 'opType', title: isRTL ? 'العملية' : 'Document' },
    { name: 'docNo', title: words?.no },
    { name: 'desc', title: words?.description },
    { name: 'amount', title: words?.amount },
    { name: 'attachments', title: isRTL ? 'المرفقات' : 'Attachments' },
  ]);

  const [tableColumnExtensions]: any = useState([
    { columnName: 'time', width: 150 },
    { columnName: 'opType', width: 200 },
    { columnName: 'docNo', width: 150 },
    { columnName: 'desc', width: 400 },
    { columnName: 'amount', width: 150 },
    { columnName: 'attachments', width: 300 },
  ]);

  const [tableColumnVisibilityColumnExtensions] = useState([
    { columnName: 'time', togglingEnabled: false },
  ]);

  const [loadFinances, financeData]: any = useLazyQuery(getGeneralDocs, {
    fetchPolicy: 'cache-and-network',
  });
  const refresQuery = {
    refetchQueries: [
      {
        query: getGeneralDocs,
        variables: {
          start: start ? start.setHours(0, 0, 0, 0) : undefined,
          end: end ? end.setHours(23, 59, 59, 999) : undefined,
        },
      },
    ],
  };

  useEffect(() => {
    const variables = {
      start: start ? start.setHours(0, 0, 0, 0) : undefined,
      end: end ? end.setHours(23, 59, 59, 999) : undefined,
    };
    if (start) {
      loadFinances({
        variables,
      });
    }
  }, [start, end]);

  const [addFinance] = useMutation(createGeneralDoc, refresQuery);
  const [editFinance] = useMutation(updateGeneralDoc, refresQuery);
  const [removeFinance] = useMutation(deleteGeneralDoc, refresQuery);

  useImperativeHandle(ref, () => ({
    refresh() {
      if (financeData?.refetch) {
        financeData.refetch();
      }
    },
  }));
  const { tempoptions } = useTemplate();
  const commitChanges = async ({ deleted }) => {
    if (deleted) {
      const _id = deleted[0];
      removeFinance({ variables: { _id } });
      setRows(rows.filter((row: any) => row._id !== _id));
    }
  };

  useEffect(() => {
    if (financeData?.data?.getGeneralDocs?.data) {
      const { data } = financeData.data.getGeneralDocs;
      const rdata = updateDocNumbers(data);
      setRows(rdata);
    }
  }, [financeData]);

  return (
    <Grid rows={rows} columns={columns} getRowId={getRowId}>
      <SortingState />
      <EditingState onCommitChanges={commitChanges} />
      <SearchState />
      <PagingState defaultCurrentPage={0} defaultPageSize={10} />
      <IntegratedSorting />
      <IntegratedFiltering />
      <IntegratedPaging />
      <DragDropProvider />
      <Table
        messages={{
          noData: isRTL ? 'لا يوجد بيانات' : 'no data',
        }}
        tableComponent={TableComponent}
        rowComponent={(props: any) => (
          <Table.Row {...props} style={{ height: 60 }}></Table.Row>
        )}
        columnExtensions={tableColumnExtensions}
      />

      <TableColumnReordering
        defaultOrder={[
          'time',
          'opType',
          'docNo',
          'desc',
          'amount',
          'attachments',
        ]}
      />
      <TableColumnResizing defaultColumnWidths={tableColumnExtensions} />

      <TableHeaderRow
        showSortingControls
        titleComponent={({ children }) => {
          return (
            <Typography style={{ fontSize: 14, fontWeight: 'bold' }}>
              {children}
            </Typography>
          );
        }}
      />
      <TableColumnVisibility
        columnExtensions={tableColumnVisibilityColumnExtensions}
        defaultHiddenColumnNames={[]}
      />
      <DataTypeProvider
        for={['time']}
        formatterComponent={timeFormatter}
      ></DataTypeProvider>
      <DataTypeProvider
        for={['amount']}
        formatterComponent={currencyFormatter}
      ></DataTypeProvider>
      <DataTypeProvider
        for={['docNo', 'refNo']}
        formatterComponent={samllFormatter}
      ></DataTypeProvider>
      <DataTypeProvider
        for={['opType']}
        formatterComponent={opTypeFormatter}
      ></DataTypeProvider>
      <DataTypeProvider
        for={['attachments']}
        formatterComponent={attachmentsFormatter}
      ></DataTypeProvider>
      <TableEditColumn
        showEditCommand={userroles?.financeall?.edit}
        showDeleteCommand={userroles?.financeall?.delete}
        showAddCommand={userroles?.financeall?.add}
        commandComponent={Command}
      ></TableEditColumn>
      <Toolbar
        rootComponent={(props: any) => (
          <Toolbar.Root
            style={{
              position: 'absolute',
              left: isRTL ? 40 : undefined,
              right: isRTL ? undefined : 40,
              top: 62,
              zIndex: 115,
            }}
            {...props}
          ></Toolbar.Root>
        )}
      />
      <ColumnChooser />
      <PagingPanel
        pageSizes={tempoptions?.pageall ? [5, 10, 20, 50, 0] : [5, 10, 20, 50]}
      />
      <SearchPanel
        inputComponent={(props: any) => {
          return <SearchTable isRTL={isRTL} {...props}></SearchTable>;
        }}
      />

      <PopupEditing
        theme={theme}
        addAction={addFinance}
        editAction={editFinance}
      >
        <PopupDocKaid company={company}></PopupDocKaid>
      </PopupEditing>
    </Grid>
  );
});

export default DocKaid;
